import React, {useEffect, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Modal,
    TextField,
    Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EditIcon from '@mui/icons-material/Edit';
import TokenIcon from '@mui/icons-material/Token';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import {auth, signOut, updateProfile} from '../firebase';
import {getTokensState} from '../services/tokenService';
import {getPaymentDetails, getPaymentHistory} from '../services/paymentService';

const Profile = ({setUser, isOpen, handleClose}) => {
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [tokens, setTokens] = useState(0);
    const [totalTokens, setTotalTokens] = useState(0);
    const [paymentDetails, setPaymentDetails] = useState({});
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [error, setError] = useState('');
    const [isEditCardModalOpen, setIsEditCardModalOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const currentUser = auth.currentUser;
            if (currentUser) {
                setName(currentUser.displayName || '');
                setPhoneNumber(currentUser.phoneNumber || '');
            }

            getTokensState()
                .then(({tokensLeft, tokensTotal}) => {
                    setTokens(tokensLeft);
                    setTotalTokens(tokensTotal);
                })
                .catch((error) => console.error('Error fetching tokens:', error));

            getPaymentDetails()
                .then((details) => setPaymentDetails(details))
                .catch((error) => console.error('Error fetching payment details:', error));

            getPaymentHistory()
                .then((history) => setPaymentHistory(history))
                .catch((error) => console.error('Error fetching payment history:', error));
        }
    }, [isOpen]);

    const handleNameChange = async () => {
        if (name.trim().length < 2) {
            setError('שם התצוגה חייב להכיל לפחות 2 אותיות.');
            return;
        }

        try {
            await updateProfile(auth.currentUser, {displayName: name});
            setUser({...auth.currentUser, displayName: name});
            setError('');
            alert('שם התצוגה עודכן בהצלחה!');
        } catch (error) {
            console.error('Error updating display name:', error);
            setError('שגיאה בעדכון שם התצוגה.');
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setUser(null);
            handleClose(); // Close the modal on sign out
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleEditCard = () => {
        setIsEditCardModalOpen(true);
    };

    const handleEditCardClose = () => {
        setIsEditCardModalOpen(false);
    };

    return (
        <>
            <Modal
                open={isOpen}
                onClose={handleClose}
                aria-labelledby="profile-modal-title"
                aria-describedby="profile-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%', // 90% of the viewport width for mobile responsiveness
                    maxWidth: 600, // Set a maximum width for larger screens
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 3,
                    borderRadius: '8px',
                    overflowY: 'auto', // Enable scrolling for content overflow
                    maxHeight: '90vh', // Ensure modal fits within the viewport height
                    direction: 'rtl', // Ensure RTL support
                }}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 3}}>
                            פרופיל אישי
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon/>
                        </IconButton>
                    </Box>

                    <Divider sx={{marginBottom: '20px'}}/>

                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                <Avatar sx={{bgcolor: 'primary.main', ml: 2}}>
                                    <AccountCircleIcon/>
                                </Avatar>
                                <TextField
                                    label="שם תצוגה"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    fullWidth
                                    sx={{direction: 'rtl'}} // Ensure RTL support
                                />
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                <Avatar sx={{bgcolor: 'primary.main', ml: 2}}>
                                    <PhoneIcon/>
                                </Avatar>
                                <TextField
                                    label="מספר טלפון"
                                    value={phoneNumber}
                                    disabled
                                    fullWidth
                                    sx={{direction: 'rtl'}} // Ensure RTL support
                                />
                            </Box>
                            {error && <Typography color="error">{error}</Typography>}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNameChange}
                                fullWidth
                            >
                                עדכן שם
                            </Button>
                        </CardContent>
                    </Card>

                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center'}}>
                                <TokenIcon sx={{fontSize: 30, color: 'secondary.main', ml: 1}}/>
                                יתרת טוקנים
                            </Typography>
                            <Typography variant="body1" sx={{direction: 'rtl'}}>
                                {totalTokens} / {tokens}
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card sx={{mb: 2}}>
                        <CardContent sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center'}}>
                                    <CreditCardIcon sx={{fontSize: 30, color: 'secondary.main', ml: 1}}/>
                                    פרטי תשלום
                                </Typography>
                                <Typography variant="body1" sx={{direction: 'rtl'}}>
                                    כרטיס אשראי: {paymentDetails.cardLast4}
                                </Typography>
                                <Typography variant="body1" sx={{direction: 'rtl'}}>
                                    תוקף: {paymentDetails.expiry}
                                </Typography>
                            </Box>
                            <IconButton onClick={handleEditCard}>
                                <EditIcon/>
                            </IconButton>
                        </CardContent>
                    </Card>

                    <Card sx={{mb: 2}}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center'}}>
                                <HistoryIcon sx={{fontSize: 30, color: 'secondary.main', ml: 1}}/>
                                היסטוריית תשלומים
                            </Typography>
                            <List sx={{direction: 'rtl'}}>
                                {paymentHistory.length === 0 ? (
                                    <Typography>אין היסטוריית תשלומים.</Typography>
                                ) : (
                                    paymentHistory.map((payment, index) => (
                                        <ListItem key={index}>
                                            <ListItemAvatar>
                                                <Avatar>
                                                    <CreditCardIcon/>
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={`תאריך: ${payment.date}`}
                                                secondary={`סכום: ₪${payment.amount} | סטטוס: ${payment.status}`}
                                                sx={{textAlign: 'right'}} // Align text to the right for RTL
                                            />
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </CardContent>
                    </Card>

                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleSignOut}
                        startIcon={<LogoutIcon/>}
                        fullWidth
                        sx={{direction: 'rtl', gap: '10px'}} // Ensure RTL support
                    >
                        התנתק
                    </Button>
                </Box>
            </Modal>

            {/* Modal for editing credit card details */}
            <Modal
                open={isEditCardModalOpen}
                onClose={handleEditCardClose}
                aria-labelledby="edit-card-modal-title"
                aria-describedby="edit-card-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 400,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    direction: 'rtl',
                }}>
                    <Typography id="edit-card-modal-title" variant="h6" component="h2"
                                sx={{textAlign: 'center', mb: 2}}>
                        ערוך פרטי כרטיס אשראי
                    </Typography>
                    <TextField
                        label="מספר כרטיס"
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="תוקף"
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="קוד CVV"
                        fullWidth
                        margin="normal"
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{mt: 2}}
                    >
                        עדכן כרטיס
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

export default Profile;
